// Roster transformation utilities - convert API response to grid format

import moment from 'moment-timezone';
import {
    RosterApiResponse,
    UserDateRow,
    DateColumn,
    ShiftMetadata,
    RosterShift
} from './types';
import {
    formatShiftDuration, isLeaveShift, isPartialLeave, getPartialLeaveDuration
} from './shiftUtils';
import type { TimeFormatPreference } from '@/utils/timeFormat';

/**
 * Transform options for roster grid
 */
interface TransformOptions {
    timezone: string;
    startDate: string;
    endDate: string;
    leaveTasks?: Array<{ id: string; name: string }>;
    hasViewLeaveTypePermission?: boolean;
    timeFormatPreference?: TimeFormatPreference | null;
}

/**
 * Get list of dates in range
 */
function getDateRange(startDate: Date, endDate: Date, timezone: string): DateColumn[] {
    const dates: DateColumn[] = [];
    const current = moment(startDate).tz(timezone).startOf('day');
    const end = moment(endDate).tz(timezone).endOf('day');

    while (current.isBefore(end)) {
        const dateKey = current.format('YYYY-MM-DD');
        const dayOfWeek = current.day();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        dates.push({
            field: dateKey,
            headerName: current.format('DD-MMM (ddd)'), // "03-Nov (Sun)"
            date: current.toDate(),
            isWeekend
        });

        current.add(1, 'day');
    }

    return dates;
}

/**
 * Helper: Check if shift is hidden leave
 * Returns true if:
 * - Shift has only one leave task with hideLeaveInAgentViews = true
 * - ALL tasks are leave tasks with hideLeaveInAgentViews = true
 * Returns false:
 * - Shift has no tasks
 * - Shift has multiple tasks, at least one of which is not a hidden leave
 */
export function isShiftHiddenLeave(shift: RosterShift): boolean {
    const tasks = shift.shiftTasks || [];
    
    // No tasks - don't hide
    if (tasks.length === 0) {
        return false;
    }
    
    // Check if ALL tasks are hidden leaves
    return tasks.every((task) =>
        Boolean(task?.taskType?.isLeave && task?.taskType?.hideLeaveInAgentViews)
    );
}

/**
 * Helper: Get location metadata from shift
 */
function getLocationMetadata(shift: RosterShift): { id: string; name: string; color?: string } | undefined {
    if (!shift.location?.id) {
        return undefined;
    }
    
    return {
        id: shift.location.id,
        name: shift.location.name || '',
        color: shift.location.color || shift.location.colour
    };
}

/**
 * Helper: Format shift comment with proper structure
 * @param shift - Roster shift object
 * @param timezone - User timezone
 * @param timeFormatPreference - User's time format preference (12h or 24h)
 */
function formatShiftComment(
    shift: RosterShift,
    timezone: string,
    timeFormatPreference?: TimeFormatPreference | null
): string | undefined {
    if (!shift.comment) return undefined;
    
    const is12HourFormat = timeFormatPreference?.timeFormat === '12h';
    const timeFormat = is12HourFormat ? 'h:mm A' : 'HH:mm';
    
    const startTime = moment.tz(shift.startTime, timezone).format(timeFormat);
    const endTime = moment.tz(shift.endTime, timezone).format(timeFormat);
    const shiftTime = `${startTime} - ${endTime}`;
    
    // Format: "Shift: 9:00 AM - 5:00 PM\nComment: This is a comment..."
    return `Shift: ${shiftTime}\nComment: ${shift.comment}`;
}

/**
 * Helper: Create shift metadata for a shift
 * Includes partial leave detection and duration extraction
 *
 * @param shift - The roster shift object
 * @param options - Configuration options
 * @param options.timezone - User timezone for time formatting
 * @param options.timeFormatPreference - User's time format preference (12h or 24h)
 * @param options.leaveTasks - Array of leave task definitions (for legacy API)
 * @param options.hasViewLeaveTypePermission - Whether user can view specific leave type names
 * @returns ShiftMetadata object with all relevant shift information
 */
function createShiftMetadata(
    shift: RosterShift,
    options: {
        timezone: string;
        timeFormatPreference?: TimeFormatPreference | null;
        leaveTasks?: Array<{ id: string; name: string }>;
        hasViewLeaveTypePermission?: boolean;
    }
): ShiftMetadata {
    const isLeave = isLeaveShift(shift);
    const partialLeave = isPartialLeave(shift);
    const partialLeaveDuration = partialLeave
        ? getPartialLeaveDuration(
            shift,
            {
                timezone: options.timezone,
                leaveTasks: options.leaveTasks || [],
                hasViewLeaveTypePermission: options.hasViewLeaveTypePermission || false,
                timeFormatPreference: options.timeFormatPreference
            }
        )
        : undefined;
    
    return {
        displayText: '',
        isHidden: isShiftHiddenLeave(shift),
        isLeave,
        isPartialLeave: partialLeave,
        partialLeaveDuration,
        shiftTemplateId: shift.shiftTemplate?.id?.toString() || shift.shiftTemplateId,
        shiftTemplateColor: shift.shiftTemplate?.color || shift.shiftTemplate?.colour,
        shiftTemplateName: shift.shiftTemplate?.name,
        shiftComment: formatShiftComment(shift, options.timezone, options.timeFormatPreference), // Only show tooltip for shifts with comments
        location: getLocationMetadata(shift)
    };
}

/**
 * Helper: Check if cell is empty
 */
function isCellEmpty(value: string | number | ShiftMetadata): boolean {
    return value === '' || (typeof value === 'object' && value.displayText === '');
}

/**
 * Helper: Build concatenated text
 */
function buildConcatenatedText(existingText: string, newShiftDisplay: string): string {
    return newShiftDisplay === '' ? existingText : `${existingText}\n${newShiftDisplay}`;
}

/**
 * Helper: Update cell value with new shift
 */
function updateCellValue(
    currentValue: string | number | ShiftMetadata,
    newShiftDisplay: string,
    newMetadata: ShiftMetadata
): ShiftMetadata {
    // First shift on this day
    if (isCellEmpty(currentValue)) {
        return { ...newMetadata, displayText: newShiftDisplay };
    }
    
    // Multiple shifts - store each shift's metadata separately for proper color rendering
    const existingText = typeof currentValue === 'object' ? currentValue.displayText : String(currentValue);
    const newText = buildConcatenatedText(existingText, newShiftDisplay);
    
    // Build multipleShifts array to preserve each shift's metadata
    if (typeof currentValue === 'object') {
        const existingShifts = currentValue.multipleShifts || [
            {
                displayText: currentValue.displayText,
                isLeave: currentValue.isLeave,
                isPartialLeave: currentValue.isPartialLeave,
                partialLeaveDuration: currentValue.partialLeaveDuration,
                shiftTemplateId: currentValue.shiftTemplateId,
                shiftTemplateName: currentValue.shiftTemplateName,
                shiftTemplateColor: currentValue.shiftTemplateColor,
                shiftComment: currentValue.shiftComment,
                location: currentValue.location
            }
        ];
        
        return {
            ...currentValue,
            displayText: newText,
            multipleShifts: [
                ...existingShifts,
                {
                    displayText: newShiftDisplay,
                    isLeave: newMetadata.isLeave,
                    isPartialLeave: newMetadata.isPartialLeave,
                    partialLeaveDuration: newMetadata.partialLeaveDuration,
                    shiftTemplateId: newMetadata.shiftTemplateId,
                    shiftTemplateName: newMetadata.shiftTemplateName,
                    shiftTemplateColor: newMetadata.shiftTemplateColor,
                    shiftComment: newMetadata.shiftComment,
                    location: newMetadata.location
                }
            ]
        };
    }
    
    // Fallback for non-object currentValue
    return { ...newMetadata, displayText: newText };
}

/**
 * Helper: Process a single shift for a user row
 */
function processShift(
    shift: RosterShift,
    userRow: UserDateRow,
    dateColumns: DateColumn[],
    options: TransformOptions & { userEndAt?: string | null }
): void {
    const shiftDay = moment(shift.startTime).tz(options.timezone).format('YYYY-MM-DD');
    
    // Check if shift is within date range
    const isInDateRange = dateColumns.some((col) => col.field === shiftDay);
    if (!isInDateRange) {
        return;
    }

    // Skip hidden leave shifts at the individual shift level.
    // This prevents hidden leaves from affecting other shifts on the same day.
    // Consistent with Timeline View which also skips hidden leaves per-shift.
    if (isShiftHiddenLeave(shift)) {
        return;
    }

    // Note: Employment end date filtering is handled earlier in filterShiftsByUserEndAt()
    // using employment timezone for accurate calendar date comparison

    // Format shift duration
    const shiftDisplay = formatShiftDuration(
        shift,
        options.timezone,
        {
            leaveTasks: options.leaveTasks || [],
            hasViewLeaveTypePermission: options.hasViewLeaveTypePermission || false,
            timeFormatPreference: options.timeFormatPreference
        }
    );
    
    // Create shift metadata
    const shiftMetadata = createShiftMetadata(
        shift,
        {
            timezone: options.timezone,
            timeFormatPreference: options.timeFormatPreference,
            leaveTasks: options.leaveTasks,
            hasViewLeaveTypePermission: options.hasViewLeaveTypePermission
        }
    );
    shiftMetadata.displayText = shiftDisplay;
    
    // Update user's date column
    if (userRow[shiftDay] !== undefined) {
        const currentValue = userRow[shiftDay];
        userRow[shiftDay] = updateCellValue(currentValue, shiftDisplay, shiftMetadata);
    }
}

/**
 * Helper: Create user row with initialized date columns
 */
function createUserRow(
    userData: { user: { id: number; email: string; name?: string; teamName?: string; teamId?: number; employeeId?: string | number } },
    dateColumns: DateColumn[]
): UserDateRow {
    const userId = String(userData.user.id);
    const userEmail = userData.user.email;
    const teamId = userData.user?.teamId || 0;
    const teamName = userData.user?.teamName || '';
    const userDisplayName = userData.user.name || userData.user.email;
    const employeeId = userData.user?.employeeId || '';
    
    const userRow: UserDateRow = {
        userId,
        userEmail,
        userDisplayName,
        teamId,
        teamName,
        employeeId
    };
    
    // Initialize all date columns
    for (const dateCol of dateColumns) {
        userRow[dateCol.field] = '';
    }
    
    return userRow;
}

/**
 * Helper: Process all shifts for a user
 */
function processUserShifts(
    userData: {
        user: {
            id: number;
            email: string;
            name?: string;
            teamName?: string;
            teamId?: number;
            employeeId?: string;
            endAt?: string | null;  // Employment end date
        };
        shifts: RosterShift[];
    },
    dateColumns: DateColumn[],
    options: TransformOptions
): UserDateRow {
    const userRow = createUserRow(userData, dateColumns);
    
    // Get user's employment end date
    const userEndAt = userData.user.endAt;
    
    // Create options object once before loop for performance
    const userOptions = { ...options, userEndAt };
    
    if (userData.shifts && Array.isArray(userData.shifts)) {
        for (const shift of userData.shifts) {
            processShift(shift, userRow, dateColumns, userOptions);
        }
    }
    
    return userRow;
}

/**
 * Helper: Internal implementation of transform logic
 */
function transformRosterInternal(
    rosterResponse: RosterApiResponse,
    options: TransformOptions
): {
    rows: UserDateRow[];
    dateColumns: DateColumn[];
} {
    // Get date columns - always generate them even if there's no data
    const start = new Date(options.startDate);
    const end = new Date(options.endDate);
    const dateColumns = getDateRange(start, end, options.timezone);
    
    if (!rosterResponse.data || rosterResponse.data.length === 0) {
        return { rows: [], dateColumns };
    }
        
    // Build map of users and their shifts
    const usersMap = new Map<string, UserDateRow>();
    
    for (const userData of rosterResponse.data) {
        const userRow = processUserShifts(userData, dateColumns, options);
        usersMap.set(userRow.userId, userRow);
    }
            
    // Convert to array and sort by display name
    const rows = Array.from(usersMap.values()).sort((a, b) =>
        a.userDisplayName.toLowerCase().localeCompare(b.userDisplayName.toLowerCase())
    );
        
    return { rows, dateColumns };
}

/**
 * Transform roster API response to user-date grid format (new API with options)
 */
export function transformRosterToUserDateGrid(
    rosterResponse: RosterApiResponse,
    options: TransformOptions
): { rows: UserDateRow[]; dateColumns: DateColumn[] };

/**
 * Transform roster API response to user-date grid format (legacy API with individual params)
 * @deprecated Use the options object signature instead
 */
export function transformRosterToUserDateGrid(
    rosterResponse: RosterApiResponse,
    timezone: string,
    startDate: string,
    endDate: string,
    leaveTasks?: Array<{ id: string; name: string }>,
    hasViewLeaveTypePermission?: boolean
): { rows: UserDateRow[]; dateColumns: DateColumn[] };

export function transformRosterToUserDateGrid(
    rosterResponse: RosterApiResponse,
    timezoneOrOptions: string | TransformOptions,
    ...rest: unknown[]
): { rows: UserDateRow[]; dateColumns: DateColumn[] } {
    let options: TransformOptions;

    if (typeof timezoneOrOptions === 'string') {
        // Old API: individual parameters are in rest
        const startDate = rest[0] as string | undefined;
        const endDate = rest[1] as string | undefined;
        const leaveTasks = rest[2] as Array<{ id: string; name: string }> | undefined;
        const hasViewLeaveTypePermission = rest[3] as boolean | undefined;

        options = {
            timezone: timezoneOrOptions,
            startDate: startDate!,
            endDate: endDate!,
            leaveTasks,
            hasViewLeaveTypePermission
        };
    } else {
        // New API: options object
        options = timezoneOrOptions;
    }

    return transformRosterInternal(rosterResponse, options);
}
